import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getTenOutsConfig,
  setTenOutsLocked,
  clearTenOutsLocked,
  formatTenOuts,
  t,
} from "@/lib/mr";

/**
 * TenOutsTrick — third-decoy effect.
 *
 * The magician hands the phone to the spectator with the screen facing
 * the magician. The display silently cycles through up to 10 preset
 * words (one per `interval` seconds). At any moment the magician can
 * cue the spectator to flip the phone toward themselves; when the
 * device's compass-heading (alpha) changes by ≥ 90° from baseline,
 * the cycle stops and the word that was on-screen at that instant
 * is rendered into the prediction sentence template.
 *
 * Heading is read via the standard `deviceorientation` event. On iOS
 * 13+ we must call `requestPermission()` from a user gesture — the
 * tap on the third decoy row IS that gesture, so this component
 * requests permission inside its mount effect (which runs synchronously
 * with the click that caused the navigation).
 */
export default function TenOutsTrick() {
  const navigate = useNavigate();
  const location = useLocation();
  const entryTitle =
    (location.state && typeof location.state.entryTitle === "string")
      ? location.state.entryTitle
      : "";

  const [config] = useState(() => getTenOutsConfig());
  const words = useMemo(() => {
    const list = config.words.map((w) => (w || "").trim()).filter(Boolean);
    return list.length > 0 ? list : ["—"];
  }, [config.words]);

  const [cycleIdx, setCycleIdx] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockedWord, setLockedWord] = useState("");

  // Track gesture-permission status separately so we can show a one-time
  // "Allow motion" pill on iOS instead of failing silently.
  const [needsPermission, setNeedsPermission] = useState(false);

  // --- Cycle ---------------------------------------------------------
  const cycleTimer = useRef(null);
  useEffect(() => {
    if (locked) return;
    cycleTimer.current = setInterval(() => {
      setCycleIdx((i) => (i + 1) % words.length);
    }, Math.max(2, config.interval) * 1000);
    return () => {
      if (cycleTimer.current) clearInterval(cycleTimer.current);
      cycleTimer.current = null;
    };
  }, [locked, config.interval, words.length]);

  // --- Orientation listener ----------------------------------------
  // Baseline alpha is captured 1.2s after start so any motion from
  // handing the phone over has settled.
  const baselineRef = useRef(null);
  const lastHandlerRef = useRef(null);
  const lockingRef = useRef(false);

  const performLock = useCallback((word) => {
    if (lockingRef.current) return;
    lockingRef.current = true;
    setLockedWord(word);
    setLocked(true);
    setTenOutsLocked(word);
  }, []);

  // Pull the current word at the moment we need to lock — using a ref
  // so the orientation handler stays referentially stable across renders.
  const cycleIdxRef = useRef(cycleIdx);
  useEffect(() => { cycleIdxRef.current = cycleIdx; }, [cycleIdx]);
  const wordsRef = useRef(words);
  useEffect(() => { wordsRef.current = words; }, [words]);

  // Angular distance on the [0, 360) compass — picks the shorter arc.
  const arc = (a, b) => {
    let d = Math.abs(a - b) % 360;
    if (d > 180) d = 360 - d;
    return d;
  };

  const attachOrientation = useCallback(() => {
    if (lastHandlerRef.current) return;
    const handler = (ev) => {
      // alpha = compass heading [0, 360); webkitCompassHeading is iOS's
      // calibrated equivalent.
      const a =
        typeof ev.webkitCompassHeading === "number"
          ? ev.webkitCompassHeading
          : (typeof ev.alpha === "number" ? ev.alpha : null);
      if (a === null || Number.isNaN(a)) return;

      if (baselineRef.current === null) {
        // First reading — wait one more sample then anchor.
        baselineRef.current = { anchor: null, samples: 1 };
        return;
      }
      // Anchor after a small warm-up so the spectator settling the phone
      // doesn't pre-trigger. We use an explicit `null` sentinel (rather
      // than a falsy check) so an anchor value of exactly 0 — a perfectly
      // valid compass heading — is still recognised as "set".
      if (baselineRef.current.anchor === null) {
        baselineRef.current.samples += 1;
        if (baselineRef.current.samples >= 6) {
          baselineRef.current.anchor = a;
        }
        return;
      }
      const d = arc(baselineRef.current.anchor, a);
      if (d >= 90) {
        const idx = cycleIdxRef.current;
        const w = wordsRef.current[idx] || "";
        performLock(w);
      }
    };
    window.addEventListener("deviceorientation", handler, true);
    // iOS sometimes only delivers `deviceorientationabsolute` events.
    window.addEventListener("deviceorientationabsolute", handler, true);
    lastHandlerRef.current = handler;
  }, [performLock]);

  // Detach on unmount or once locked.
  useEffect(() => {
    return () => {
      if (lastHandlerRef.current) {
        window.removeEventListener("deviceorientation", lastHandlerRef.current, true);
        window.removeEventListener("deviceorientationabsolute", lastHandlerRef.current, true);
        lastHandlerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (!locked) return;
    if (lastHandlerRef.current) {
      window.removeEventListener("deviceorientation", lastHandlerRef.current, true);
      window.removeEventListener("deviceorientationabsolute", lastHandlerRef.current, true);
      lastHandlerRef.current = null;
    }
    if (cycleTimer.current) {
      clearInterval(cycleTimer.current);
      cycleTimer.current = null;
    }
  }, [locked]);

  // --- iOS permission flow -----------------------------------------
  useEffect(() => {
    // Standard event — no permission needed.
    const canRequest =
      typeof window !== "undefined" &&
      typeof window.DeviceOrientationEvent !== "undefined" &&
      typeof window.DeviceOrientationEvent.requestPermission === "function";

    if (!canRequest) {
      attachOrientation();
      return;
    }
    // iOS — DecoyNotes already fired requestPermission() inside the
    // synchronous row-tap gesture (the only context where iOS Safari
    // will honour it). Read its outcome from the transient flag.
    let priorState = null;
    try { priorState = localStorage.getItem("mr_orientation_perm"); } catch { /* ignore */ }
    if (priorState === "granted") {
      attachOrientation();
      return;
    }
    if (priorState === "denied") {
      setNeedsPermission(true);
      return;
    }
    // No prior decision yet — try once (some Safari versions still
    // accept this within a few hundred ms of the original tap).
    window.DeviceOrientationEvent.requestPermission()
      .then((state) => {
        try {
          localStorage.setItem(
            "mr_orientation_perm",
            state === "granted" ? "granted" : "denied",
          );
        } catch { /* ignore */ }
        if (state === "granted") attachOrientation();
        else setNeedsPermission(true);
      })
      .catch(() => setNeedsPermission(true));
  }, [attachOrientation]);

  const onRequestPermission = useCallback(async () => {
    try {
      const state = await window.DeviceOrientationEvent.requestPermission();
      try {
        localStorage.setItem(
          "mr_orientation_perm",
          state === "granted" ? "granted" : "denied",
        );
      } catch { /* ignore */ }
      if (state === "granted") {
        setNeedsPermission(false);
        baselineRef.current = null;
        attachOrientation();
      }
    } catch {
      /* user dismissed — leave the pill on so they can retry */
    }
  }, [attachOrientation]);

  // --- Exit handling ----------------------------------------------
  const handleBack = useCallback(() => {
    clearTenOutsLocked();
    navigate("/notes");
  }, [navigate]);

  // --- Render ------------------------------------------------------
  const currentWord = locked ? lockedWord : words[cycleIdx % words.length];
  const sentence = formatTenOuts(config.template, currentWord);

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-white text-black flex flex-col"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif',
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="tenouts-page"
    >
      {/* Top toolbar — minimal, matches the rest of the disguise. */}
      <div className="relative flex items-center justify-between px-3 pt-3 pb-2 border-b border-neutral-100 select-none">
        <button
          type="button"
          onClick={handleBack}
          aria-label="back to notes"
          className="relative z-40 flex items-center text-[17px] text-[#FFB400] font-normal bg-transparent border-0 p-0 cursor-pointer active:opacity-70"
          data-testid="tenouts-back-button"
        >
          <svg width="13" height="22" viewBox="0 0 13 22" fill="none" aria-hidden="true">
            <path d="M11 1L2 11L11 21" stroke="#FFB400" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="ml-1.5">{t("notes")}</span>
        </button>
      </div>

      {/* Title + date — kept understated. */}
      <div className="px-5 pt-5 pb-2 shrink-0">
        <div className="w-full text-[26px] font-bold leading-tight text-black" data-testid="tenouts-title">
          {entryTitle || ""}
        </div>
        {/* Prediction sentence — sits where a real Notes body would start
            so the magician can read it from across the table. */}
        <div
          className="mt-3 text-[17px] leading-[1.45] text-black select-none min-h-[1.45em]"
          data-testid="tenouts-sentence"
        >
          {sentence || ""}
        </div>
      </div>

      {/* Performance body — empty by design. The magician reads the
          cycling word from the prediction sentence above; once the
          spectator flips the phone, the locked sentence is all they
          see, so it reads as a perfectly normal short note. */}
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          {/* iOS one-time motion-permission pill. */}
          {needsPermission && !locked && (
            <button
              type="button"
              onClick={onRequestPermission}
              className="mt-8 px-4 py-2 text-[13px] rounded-full bg-neutral-100 text-neutral-600 border border-neutral-200"
              data-testid="tenouts-allow-motion-btn"
            >
              Allow motion access
            </button>
          )}

          {/* Hidden mount node so tests can still introspect the current
              word without it being visible on screen. */}
          <span className="sr-only" data-testid="tenouts-current-word">
            {currentWord || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}
